import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Pencil } from 'lucide-react';
import { isSuperAdmin } from '../lib/utils';

import { fmtCurrency, getFYOptions } from './agent-commission/helpers';
import AgentListView from './agent-commission/AgentListView';
import TabsControlBar from './agent-commission/TabsControlBar';
import InvoiceTab from './agent-commission/InvoiceTab';
import ChallanTab from './agent-commission/ChallanTab';
import LedgerTab from './agent-commission/LedgerTab';
import AgingTab from './agent-commission/AgingTab';
import EditCommissionDialog from './agent-commission/EditCommissionDialog';
import PaymentDialog from './agent-commission/PaymentDialog';
import BulkPaymentDialog from './agent-commission/BulkPaymentDialog';
import DownloadDialog from './agent-commission/DownloadDialog';
import BillDetailDialog from './agent-commission/BillDetailDialog';

const AgentCommission = ({ user }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [fyOptions] = useState(getFYOptions());
  const [activeFY, setActiveFY] = useState(getFYOptions()[0]);
  const [expandedEntities, setExpandedEntities] = useState({});
  const [editDialog, setEditDialog] = useState(null);
  const [editPct, setEditPct] = useState('');
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [selectedBills, setSelectedBills] = useState(new Set());
  const [showCarryForward, setShowCarryForward] = useState(false);
  const [bulkPayDialog, setBulkPayDialog] = useState(false);
  const [bulkPayDate, setBulkPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [bulkPayNotes, setBulkPayNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('invoice');
  const [ledger, setLedger] = useState(null);
  const [aging, setAging] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [agingLoading, setAgingLoading] = useState(false);
  const [challanTabVisible, setChallanTabVisible] = useState(true);
  const [challanVisLoading, setChallanVisLoading] = useState(false);
  const [downloadDialog, setDownloadDialog] = useState(false);
  const [dlFilterType, setDlFilterType] = useState('all');
  const [dlFormat, setDlFormat] = useState('pdf');
  const [dlPayDateStart, setDlPayDateStart] = useState('');
  const [dlPayDateEnd, setDlPayDateEnd] = useState('');
  const [billDetail, setBillDetail] = useState(null);
  const [billDetailLoading, setBillDetailLoading] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [ledgerFilter, setLedgerFilter] = useState('all');
  const [showEmptyParties, setShowEmptyParties] = useState(false);
  const [expandedLedgerGroups, setExpandedLedgerGroups] = useState({});

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdminUser = isSuperAdmin(user?.role);

  useEffect(() => { loadAgents(); }, []);

  // Load challan tab visibility setting
  useEffect(() => {
    const loadChallanVis = async () => {
      try {
        const res = await api.get('/settings/challan-visibility');
        setChallanTabVisible(res.data.se_challan_visible !== false);
      } catch { /* default visible */ }
    };
    if (isAdmin) loadChallanVis();
  }, [isAdmin]);

  const loadAgents = async () => {
    setListLoading(true);
    try { const res = await api.get('/agent-commission/agents'); setAgents(res.data); }
    catch { toast.error('Failed to load agents'); }
    finally { setListLoading(false); }
  };

  const loadReport = useCallback(async (agentId, fy) => {
    if (!agentId) return;
    setLoading(true);
    setSelectedBills(new Set());
    try {
      const res = await api.get(`/agent-commission/${agentId}?start_date=${fy.start}&end_date=${fy.end}`);
      setReport(res.data);
      const expanded = {};
      res.data.entries.forEach(e => { expanded[e.entity_id] = true; });
      setExpandedEntities(expanded);
    } catch { toast.error('Failed to load commission report'); }
    finally { setLoading(false); }
  }, []);

  const loadLedger = async (agentId, fy) => {
    setLedgerLoading(true);
    try {
      const params = fy ? `?start_date=${fy.start}&end_date=${fy.end}` : '';
      const res = await api.get(`/agent-commission/${agentId}/payment-ledger${params}`);
      setLedger(res.data);
    }
    catch { toast.error('Failed to load ledger'); }
    finally { setLedgerLoading(false); }
  };

  const loadAging = async (agentId, fy) => {
    setAgingLoading(true);
    try {
      const params = fy ? `?start_date=${fy.start}&end_date=${fy.end}` : '';
      const res = await api.get(`/agent-commission/${agentId}/aging${params}`);
      setAging(res.data);
    }
    catch { toast.error('Failed to load aging report'); }
    finally { setAgingLoading(false); }
  };

  const selectAgent = (agent) => {
    setSelectedAgent(agent);
    setReport(null);
    setLedger(null);
    setAging(null);
    setActiveTab('invoice');
    loadReport(agent.id, activeFY);
  };

  const goBack = () => {
    setSelectedAgent(null);
    setReport(null);
    setLedger(null);
    setAging(null);
    setSelectedBills(new Set());
    loadAgents();
  };

  const handleFYChange = (fy) => {
    setActiveFY(fy);
    setShowCustomRange(false);
    if (!selectedAgent) return;
    setLedger(null);
    setAging(null);
    if (activeTab === 'ledger') loadLedger(selectedAgent.id, fy);
    else if (activeTab === 'aging') loadAging(selectedAgent.id, fy);
    else loadReport(selectedAgent.id, fy);
  };

  const applyCustomRange = () => {
    if (!customStart || !customEnd) { toast.error('Pick both start and end dates'); return; }
    if (customStart > customEnd) { toast.error('Start date must be before end date'); return; }
    const fmt = (d) => {
      const date = new Date(d);
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yy = String(date.getFullYear()).slice(2);
      return `${dd}/${mm}/${yy}`;
    };
    const fy = { label: `${fmt(customStart)} – ${fmt(customEnd)}`, start: customStart, end: customEnd, isCustom: true };
    setActiveFY(fy);
    if (!selectedAgent) return;
    setLedger(null);
    setAging(null);
    if (activeTab === 'ledger') loadLedger(selectedAgent.id, fy);
    else if (activeTab === 'aging') loadAging(selectedAgent.id, fy);
    else loadReport(selectedAgent.id, fy);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'ledger' && !ledger && selectedAgent) loadLedger(selectedAgent.id, activeFY);
    if (tab === 'aging' && !aging && selectedAgent) loadAging(selectedAgent.id, activeFY);
  };

  const toggleEntity = (id) => setExpandedEntities(prev => ({ ...prev, [id]: !prev[id] }));

  const openBillDetail = async (bill) => {
    setBillDetailLoading(true);
    setBillDetail({ type: bill.type, number: bill.number });
    try {
      const endpoint = bill.type === 'SE' ? `/sales-entries/${bill.id}` : `/sales-returns/${bill.id}`;
      const res = await api.get(endpoint);
      setBillDetail({ type: bill.type, number: bill.number, data: res.data });
    } catch (err) {
      toast.error('Failed to load bill details');
      setBillDetail(null);
    } finally { setBillDetailLoading(false); }
  };

  const openEditBill = (bill, agentId) => {
    setEditDialog({
      type: 'bill', id: bill.id, agentId,
      pct: bill.commission_pct, name: bill.number,
      pct_2: bill.commission_pct_2 || 0,
      discount_pct: bill.discount_pct || 0,
      discount_amount: bill.discount_amount || 0,
      manual_commission: bill.is_manual ? bill.commission_amount : '',
      amount: bill.amount,
      is_frozen: bill.is_frozen || false,
      payment_status: bill.payment_status,
    });
    setEditPct(String(bill.commission_pct));
  };

  const openEditCustomer = (entry) => {
    let discPct = entry.default_discount_pct || 0;
    if (!discPct && entry.bills?.length) {
      const totAmt = entry.bills.reduce((s, b) => s + (b.amount || 0), 0);
      const totDisc = entry.bills.reduce((s, b) => s + (b.discount_amount || 0), 0);
      if (totAmt > 0 && totDisc > 0) discPct = Math.round(totDisc / totAmt * 10000) / 100;
    }
    setEditDialog({
      type: 'customer', id: entry.entity_id, entityType: entry.entity_type,
      pct: entry.default_commission_pct, name: entry.entity_name,
      discount_pct: discPct,
    });
    setEditPct(String(entry.default_commission_pct));
  };

  const saveEdit = async () => {
    if (!editDialog) return;
    const pct = parseFloat(editPct);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error('Enter valid % (0-100)'); return; }
    try {
      if (editDialog.type === 'bill') {
        const body = { commission_pct: pct };
        if (editDialog.pct_2 === '' || editDialog.pct_2 === undefined || editDialog.pct_2 === null) {
          body.commission_pct_2 = -1;
        } else {
          const p2 = parseFloat(editDialog.pct_2);
          if (!isNaN(p2) && p2 >= 0 && p2 <= 100) body.commission_pct_2 = p2;
        }
        if (editDialog.discount_pct !== undefined) body.discount_pct = parseFloat(editDialog.discount_pct) || 0;
        if (editDialog.discount_amount !== undefined && editDialog.discount_amount !== '') body.discount_amount = parseFloat(editDialog.discount_amount) || 0;
        const mc = editDialog.manual_commission;
        if (mc !== undefined && mc !== '') body.manual_commission = parseFloat(mc);
        else body.manual_commission = 0;
        await api.put(`/agent-commission/override/${editDialog.id}?agent_id=${editDialog.agentId}`, body);
      } else {
        const body = { commission_pct: pct };
        if (editDialog.discount_pct !== undefined) body.default_discount_pct = parseFloat(editDialog.discount_pct) || 0;
        await api.put(`/agent-commission/customer-rate/${editDialog.id}?entity_type=${editDialog.entityType}`, body);
      }
      toast.success('Updated successfully');
      setEditDialog(null);
      loadReport(selectedAgent.id, activeFY);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const saveAgentPct2 = async (newPct2) => {
    try {
      await api.put(`/agent-commission/agent-pct2/${selectedAgent.id}`, { commission_pct_2: parseFloat(newPct2) || 0 });
      toast.success('Double commission updated');
      loadReport(selectedAgent.id, activeFY);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const openPayment = (bill, agentId) => {
    setPaymentDialog({ billId: bill.id, agentId, currentStatus: bill.payment_status });
    setPaymentDate(bill.payment_date || new Date().toISOString().slice(0, 10));
    setPaymentNotes('');
  };

  const savePayment = async (newStatus) => {
    if (!paymentDialog) return;
    try {
      await api.put(`/agent-commission/payment/${paymentDialog.billId}?agent_id=${paymentDialog.agentId}`, {
        payment_status: newStatus, payment_date: newStatus === 'paid' ? paymentDate : null, payment_notes: paymentNotes || null,
      });
      toast.success(`Marked as ${newStatus}`);
      setPaymentDialog(null);
      loadReport(selectedAgent.id, activeFY);
      setLedger(null); setAging(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
  };

  const toggleBillSelection = (billId) => {
    setSelectedBills(prev => { const n = new Set(prev); if (n.has(billId)) n.delete(billId); else n.add(billId); return n; });
  };

  const selectAllUnpaid = () => {
    if (!report) return;
    const unpaid = new Set();
    report.entries.forEach(e => { e.bills.forEach(b => { if (b.payment_status !== 'paid') unpaid.add(b.id); }); });
    setSelectedBills(unpaid);
  };

  const clearSelection = () => setSelectedBills(new Set());

  const openBulkPayDialog = () => { setBulkPayDate(new Date().toISOString().slice(0, 10)); setBulkPayNotes(''); setBulkPayDialog(true); };

  const confirmBulkPaid = async () => {
    if (selectedBills.size === 0) return;
    try {
      await api.post(`/agent-commission/bulk-mark-paid?agent_id=${selectedAgent.id}`, {
        bill_ids: Array.from(selectedBills), payment_status: 'paid', payment_date: bulkPayDate, payment_notes: bulkPayNotes || null,
      });
      toast.success(`${selectedBills.size} bills marked as paid`);
      setSelectedBills(new Set()); setBulkPayDialog(false);
      loadReport(selectedAgent.id, activeFY);
      setLedger(null); setAging(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk update failed'); }
  };

  const bulkMarkUnpaid = async () => {
    if (selectedBills.size === 0 || !isAdmin) return;
    try {
      await api.post(`/agent-commission/bulk-mark-paid?agent_id=${selectedAgent.id}`, {
        bill_ids: Array.from(selectedBills), payment_status: 'unpaid',
      });
      toast.success(`${selectedBills.size} bills marked as unpaid`);
      setSelectedBills(new Set());
      loadReport(selectedAgent.id, activeFY);
      setLedger(null); setAging(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk update failed'); }
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const a = document.createElement('a'); a.href = url;
    a.setAttribute('download', filename.replace(/ /g, '_'));
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const downloadStatement = async () => {
    try {
      const res = await api.get(`/agent-commission/${selectedAgent.id}/statement-pdf?start_date=${activeFY.start}&end_date=${activeFY.end}`, { responseType: 'blob' });
      triggerDownload(res.data, `Statement_${selectedAgent.name}_${activeFY.label}.pdf`);
      toast.success('Statement downloaded');
    } catch { toast.error('Download failed'); }
  };

  const openDownloadDialog = () => {
    setDlFilterType('all');
    setDlFormat('pdf');
    setDlPayDateStart('');
    setDlPayDateEnd('');
    setDownloadDialog(true);
  };

  const executeDownload = async () => {
    if (!selectedAgent) return;
    let params = `start_date=${activeFY.start}&end_date=${activeFY.end}&filter_type=${dlFilterType}`;
    if (dlFilterType === 'paid' && dlPayDateStart) params += `&payment_date_start=${dlPayDateStart}`;
    if (dlFilterType === 'paid' && dlPayDateEnd) params += `&payment_date_end=${dlPayDateEnd}`;
    if (dlFilterType === 'custom' && selectedBills.size > 0) params += `&bill_ids=${Array.from(selectedBills).join(',')}`;
    const ext = dlFormat === 'pdf' ? 'pdf' : 'xlsx';
    const endpoint = dlFormat === 'pdf' ? 'pdf' : 'excel';
    const filterLabel = { all: 'Full', paid: 'Paid', unpaid: 'Unpaid', custom: 'Custom' }[dlFilterType];
    try {
      const res = await api.get(`/agent-commission/${selectedAgent.id}/${endpoint}?${params}`, { responseType: 'blob' });
      triggerDownload(res.data, `Commission_${filterLabel}_${selectedAgent.name}_${activeFY.label}.${ext}`);
      toast.success(`${filterLabel} report downloaded`);
      setDownloadDialog(false);
    } catch { toast.error('Download failed'); }
  };

  const downloadAllAgentsSummary = async (format) => {
    const endpoint = format === 'pdf' ? 'pdf' : 'excel';
    const ext = format === 'pdf' ? 'pdf' : 'xlsx';
    try {
      const res = await api.get(`/agent-commission/all-agents-summary/${endpoint}`, { responseType: 'blob' });
      triggerDownload(res.data, `All_Agents_Commission_Summary.${ext}`);
      toast.success('Summary downloaded');
    } catch { toast.error('Download failed'); }
  };

  const shareWhatsApp = () => {
    if (!selectedAgent?.phone) { toast.error('Agent phone number not available'); return; }
    const s = report?.summary || {};
    const phone = selectedAgent.phone.replace(/[^0-9]/g, '');
    const phoneNum = phone.startsWith('91') ? phone : `91${phone}`;
    const msg = `*Commission Statement - ${selectedAgent.name}*\n` +
      `Period: ${activeFY.label}\n\n` +
      `Total Sales: ${fmtCurrency(s.total_sales)}\n` +
      `Total Returns: ${fmtCurrency(s.total_returns)}\n` +
      `Net Sales: ${fmtCurrency(s.net_sales)}\n` +
      `Commission Earned: ${fmtCurrency(s.total_commission)}\n` +
      `Paid: ${fmtCurrency(s.total_paid)}\n` +
      `*Balance Due: ${fmtCurrency(s.grand_total_unpaid)}*\n\n` +
      `_Statement from Garment360_`;
    window.open(`https://wa.me/${phoneNum}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const toggleChallanTab = async () => {
    setChallanVisLoading(true);
    try {
      const newVal = !challanTabVisible;
      await api.put('/settings/challan-visibility', { se_challan_visible: newVal });
      setChallanTabVisible(newVal);
      if (!newVal && activeTab === 'challan') setActiveTab('invoice');
      toast.success(newVal ? 'Challan tab enabled' : 'Challan tab hidden');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setChallanVisLoading(false); }
  };

  // ===== AGENT LIST VIEW =====
  if (!selectedAgent) {
    return (
      <AgentListView
        agents={agents}
        listLoading={listLoading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectAgent={selectAgent}
        downloadAllAgentsSummary={downloadAllAgentsSummary}
      />
    );
  }

  // ===== AGENT DETAIL VIEW =====
  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="agent-commission-detail">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={goBack} data-testid="back-to-list-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>{selectedAgent.name}</h1>
            <p className="text-gray-600 mt-1">
              {[selectedAgent.phone, selectedAgent.area, selectedAgent.city].filter(Boolean).join(' | ')}
              {selectedAgent.commission_percentage ? ` | Default: ${selectedAgent.commission_percentage}%` : ''}
              {report?.agent?.commission_pct_2 > 0 && <span className="text-indigo-600 font-medium"> + {report.agent.commission_pct_2}% (Default 2nd)</span>}
              {isAdmin && (
                <button className="ml-2 text-blue-500 hover:text-blue-700 text-xs" data-testid="edit-agent-pct2-btn"
                  onClick={async () => {
                    const val = prompt('Default 2nd Commission % for NEW / unpaid bills without override (0 to disable):', report?.agent?.commission_pct_2 || 0);
                    if (val === null) return;
                    const newPct = parseFloat(val);
                    if (isNaN(newPct) || newPct < 0 || newPct > 100) { toast.error('Invalid %'); return; }
                    try {
                      const res = await api.get(`/agent-commission/agent-pct2-impact/${selectedAgent.id}`);
                      const { affected_count, frozen_count, with_override_count } = res.data;
                      const msg = `This will update ${affected_count} bill(s) without an override.\n\nFrozen (paid) bills: ${frozen_count} — unchanged.\nBills with own 2nd % override: ${with_override_count} — unchanged.\n\nContinue?`;
                      if (!window.confirm(msg)) return;
                    } catch (_) { /* proceed even if impact fetch fails */ }
                    saveAgentPct2(val);
                  }}><Pencil className="w-3 h-3 inline" /> Default 2nd Commission %</button>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Controls: Tabs + FY + Downloads + WhatsApp */}
      <TabsControlBar
        activeTab={activeTab} handleTabChange={handleTabChange}
        fyOptions={fyOptions} activeFY={activeFY} handleFYChange={handleFYChange}
        showCustomRange={showCustomRange} setShowCustomRange={setShowCustomRange}
        customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd}
        applyCustomRange={applyCustomRange}
        report={report}
        openDownloadDialog={openDownloadDialog}
        downloadStatement={downloadStatement}
        shareWhatsApp={shareWhatsApp}
        isSuperAdminUser={isSuperAdminUser}
        challanTabVisible={challanTabVisible}
        challanVisLoading={challanVisLoading}
        toggleChallanTab={toggleChallanTab}
      />

      {/* Tabs */}
      {activeTab === 'invoice' && (
        <InvoiceTab
          loading={loading} report={report} activeFY={activeFY}
          selectedAgent={selectedAgent} isAdmin={isAdmin}
          expandedEntities={expandedEntities} toggleEntity={toggleEntity}
          selectedBills={selectedBills} toggleBillSelection={toggleBillSelection}
          clearSelection={clearSelection} selectAllUnpaid={selectAllUnpaid}
          openEditBill={openEditBill} openEditCustomer={openEditCustomer}
          openPayment={openPayment} openBillDetail={openBillDetail}
          openBulkPayDialog={openBulkPayDialog} bulkMarkUnpaid={bulkMarkUnpaid}
          showCarryForward={showCarryForward} setShowCarryForward={setShowCarryForward}
        />
      )}

      {activeTab === 'challan' && (
        <ChallanTab loading={loading} report={report} activeFY={activeFY} openBillDetail={openBillDetail} />
      )}

      {activeTab === 'ledger' && (
        <LedgerTab
          ledgerLoading={ledgerLoading} ledger={ledger}
          ledgerFilter={ledgerFilter} setLedgerFilter={setLedgerFilter}
          showEmptyParties={showEmptyParties} setShowEmptyParties={setShowEmptyParties}
          expandedLedgerGroups={expandedLedgerGroups} setExpandedLedgerGroups={setExpandedLedgerGroups}
        />
      )}

      {activeTab === 'aging' && (
        <AgingTab agingLoading={agingLoading} aging={aging} />
      )}

      {/* Dialogs */}
      <EditCommissionDialog
        editDialog={editDialog} setEditDialog={setEditDialog}
        editPct={editPct} setEditPct={setEditPct}
        saveEdit={saveEdit}
      />

      <PaymentDialog
        paymentDialog={paymentDialog} setPaymentDialog={setPaymentDialog}
        paymentDate={paymentDate} setPaymentDate={setPaymentDate}
        paymentNotes={paymentNotes} setPaymentNotes={setPaymentNotes}
        savePayment={savePayment} isAdmin={isAdmin}
      />

      <BulkPaymentDialog
        bulkPayDialog={bulkPayDialog} setBulkPayDialog={setBulkPayDialog}
        bulkPayDate={bulkPayDate} setBulkPayDate={setBulkPayDate}
        bulkPayNotes={bulkPayNotes} setBulkPayNotes={setBulkPayNotes}
        confirmBulkPaid={confirmBulkPaid}
        selectedBillsSize={selectedBills.size}
      />

      <DownloadDialog
        downloadDialog={downloadDialog} setDownloadDialog={setDownloadDialog}
        dlFilterType={dlFilterType} setDlFilterType={setDlFilterType}
        dlFormat={dlFormat} setDlFormat={setDlFormat}
        dlPayDateStart={dlPayDateStart} setDlPayDateStart={setDlPayDateStart}
        dlPayDateEnd={dlPayDateEnd} setDlPayDateEnd={setDlPayDateEnd}
        selectedBillsSize={selectedBills.size}
        activeFY={activeFY}
        executeDownload={executeDownload}
      />

      <BillDetailDialog
        billDetail={billDetail} setBillDetail={setBillDetail}
        billDetailLoading={billDetailLoading}
      />
    </div>
  );
};

export default AgentCommission;
